import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabelObject } from 'src/Library/Types';
import {
  Row,
  Checkbox,
  Form,
  Col,
  Select,
  Input,
  InputNumber,
  Button,
  message,
  Spin,
} from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import { uploadSIModalStyles } from './UploadSIModal.styles';
import {
  renderLabelAndInputNumber,
  renderLabelAndTextArea,
  numberLengthValidator,
  renderSelectDropDown,
  renderLabelAndInput,
} from 'src/Library/Common';
import { connect } from 'react-redux';
import UploadSIModalContainerDetails from './UploadSIModalContainerDetails';
import { isValidArray, clone, isValidNumber, isValidString } from 'src/Library/Helper';
import { renderWeightOrVolumeUnit, getMandatoryFields } from './UploadSIModal.utils';
import { fetchShipmentInstructionsPackageTypes , fetchShipmentInstructionsContainers } from 'src/api/shipmentExecution';
import { ShipmentData } from 'src/components/pages/shipment-execution/SE.Types';
import { isUndefined } from 'util';
import { CargoTypes } from 'src/Library/Constants';
import { CargoType } from 'src/components/pages/Enquiry/InquiryUtils';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends StylesProps<ReturnType<typeof uploadSIModalStyles>> {
  form;
  weightUnits: string[];
  volumeUnits: string[];
  volumeConversionMap;
  weightConversionMap;
  shipmentData: ShipmentData;
  prefillingData;
  pendingContainers: number;
  countryId: string;
  SICountryValidations;
  productIMOClassCodeMap: KeyLabelObject;
  trackingContainers;
  packingGroups: KeyLabelObject [];
  imoClassCodes: KeyLabelObject [];
}

interface StateProps {
  containerDetails;
  isFetchingPackages: boolean;
  packageTypes;
  containerNoList;
  isFetchingContainers: boolean;
  isPrefillingAllContainers?: boolean;
}

const UploadSIModalContainerAndCargo = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    weightUnits,
    volumeUnits,
    volumeConversionMap,
    weightConversionMap,
    shipmentData,
    prefillingData,
    pendingContainers,
    countryId,
    SICountryValidations,
    productIMOClassCodeMap,
    trackingContainers,
    packingGroups,
    imoClassCodes,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    containerDetails: isValidArray(prefillingData?.cargoAndContainerDetails?.containerDetails) ?
      prefillingData?.cargoAndContainerDetails?.containerDetails
      : [{
        sealDetails: [{ type: 'Carrier' }],
      }],
    packageTypes: [],
    isFetchingPackages: false,
    containerNoList: trackingContainers || [],
    isFetchingContainers: false,
  });

  const {
    containerDetails,
    isFetchingPackages,
    packageTypes,
    containerNoList,
    isFetchingContainers,
    isPrefillingAllContainers,
  } = state;

  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
  } = form;

  React.useEffect(() => {
    setFieldsValue({ ['cargoAndContainerDetails.containerDetails']: containerDetails });
  }, [containerDetails]);

  const renderSingeCargoCheckbox = () : React.ReactElement => {
    return (
      <Form.Item className={classes.cargoTypePatch} style={{ marginBottom: 0 }}>
        {getFieldDecorator('isOnlyOneCargo', {
          initialValue: prefillingData?.isOnlyOneCargo,
          valuePropName: 'checked',
        })(
          <Checkbox>
            I’m shipping only one cargo in this shipment
          </Checkbox>
        )}
      </Form.Item>
    );
  }

  const getPackageTypes = async () : Promise<void> => {
    if (isValidArray(packageTypes)) return;
    setState({ isFetchingPackages: true });
    const response = await fetchShipmentInstructionsPackageTypes();
    if (response.isSuccess) {
      setState({ packageTypes: response.data || [], isFetchingPackages: false });
    } else {
      message.error(response.errorMessage);
      setState({ isFetchingPackages: false });
    }
  };

  const renderPackageType = () : React.ReactElement => {
    const prefillPackageType = prefillingData?.cargoAndContainerDetails?.cargoDetails?.packageType;

    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator('cargoAndContainerDetails.cargoDetails.packageType', {
          rules: [
            { required: true, message: 'Required' },
          ],
          initialValue: prefillPackageType,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            labelInValue
            placeholder="Type"
            optionFilterProp="children"
            dropdownRender={menu => renderSelectDropDown(menu, isFetchingPackages)}
            onFocus={() => getPackageTypes()}
          >
            {packageTypes?.map(item =>
              <Select.Option key={item?.id}>{item?.description}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderSingleCargoDetails = () : React.ReactElement => {
    const labelInputProps = {
      colSpan: {
        left: 6,
        right: 8,
      },
      form,
      classes,
      precision: 2,
      min: 0,
    };
    const mandatoryFields = getMandatoryFields({
      SICountryValidations,
      countryId,
      shipmentData
    });
    const {
      isHsCodeMandatory,
      isNCMCodeMandatory,
    } = mandatoryFields;

    return (
      <Row style={{ margin: '4px 0', backgroundColor: '#fff' }}>
        <Col span={2} />
        <Col span={22}>
          <Row>
            <Col span={8}>
              {renderLabelAndInputNumber({
                ...labelInputProps,
                label: <>HS Code{isHsCodeMandatory ? <Asterisk /> : null}</>,
                key: 'cargoAndContainerDetails.cargoDetails.hsCode',
                initialValue: isValidNumber(prefillingData?.cargoAndContainerDetails?.cargoDetails?.hsCode) ?
                  prefillingData?.cargoAndContainerDetails?.cargoDetails?.hsCode :
                  shipmentData?.hsCode,
                placeholder: 'HS Code',
                precision: 0,
                rules: [{ required: isHsCodeMandatory, message: 'Required' }],
              })}
            </Col>
            <Col span={1} />
            <Col span={8}>
              <div>
                {renderLabelAndInputNumber({
                  ...labelInputProps,
                  colSpan: {
                    left: 6,
                    right: 5,
                  },
                  label: 'Gross Volume',
                  key: 'cargoAndContainerDetails.cargoDetails.grossVolume.value',
                  initialValue: prefillingData?.cargoAndContainerDetails?.cargoDetails?.grossVolume?.value,
                  placeholder: '0.00',
                  precision: 4,
                })}
                <Col span={5} style={{ paddingLeft: '6px' }}>
                  {renderWeightOrVolumeUnit(
                    form,
                    classes,
                    'cargoAndContainerDetails.cargoDetails.grossVolume.unit',
                    volumeUnits,
                    prefillingData?.cargoAndContainerDetails?.cargoDetails?.grossVolume?.unit || 'cbm',
                  )}
                </Col>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={8} className={classes.flexColumn}>
              <div>
                {renderLabelAndTextArea({
                  ...labelInputProps,
                  colSpan: {
                    left: 6,
                    right: 16,
                  },
                  label: 'Description*',
                  key: 'cargoAndContainerDetails.cargoDetails.description',
                  initialValue: prefillingData?.cargoAndContainerDetails?.cargoDetails?.description ||
                  (isValidArray(shipmentData?.productDetails) ? shipmentData?.productDetails?.name : undefined),
                  placeholder: 'Description',
                  rules: [
                    { required: true, message: 'Required' },
                    { max: 500, message: 'Maximum 500 characters allowed'},
                  ],
                })}
              </div>
              <div>
                {renderLabelAndTextArea({
                  ...labelInputProps,
                  colSpan: {
                    left: 6,
                    right: 16,
                  },
                  label: 'Marks and Numbers',
                  key: 'cargoAndContainerDetails.cargoDetails.marksAndNumbers',
                  initialValue: prefillingData?.cargoAndContainerDetails?.cargoDetails?.marksAndNumbers,
                  placeholder: 'Marks and Numbers',
                  rules: [{ max: 35, message: 'Maximum 35 characters allowed' }],
                })}
              </div>
              <div>
                {renderLabelAndInputNumber({
                  ...labelInputProps,
                  label: <>NCM Code{isNCMCodeMandatory ? <Asterisk /> : null}</>,
                  key: 'cargoAndContainerDetails.cargoDetails.ncmCode',
                  initialValue: prefillingData?.cargoAndContainerDetails?.cargoDetails?.ncmCode,
                  placeholder: 'NCM Code',
                  precision: 0,
                  rules: [
                    { required: isNCMCodeMandatory, message: 'Required' },
                    {
                      validator: (rule, value, callback) => numberLengthValidator(rule, value, callback, { max: 140 })
                    },
                  ],
                })}
                </div>
            </Col>
            <Col span={1} />
            <Col span={8} className={classes.flexColumn}>
              <div>
                <Col span={6} className={classes.label}>Package Type*</Col>
                <Col span={18}>
                  {renderPackageType()}
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ marginBottom: '4px' }} className={classes.contentLabel}>Print on B/L as</div>
                    <Form.Item>
                      {form.getFieldDecorator('cargoAndContainerDetails.cargoDetails.packageTypeOnBl', {
                        initialValue: prefillingData?.cargoAndContainerDetails?.cargoDetails?.packageTypeOnBl,
                      })(
                        <Input
                          placeholder="Type"
                          className={classes.inputField}
                          disabled={!getFieldValue('cargoAndContainerDetails.cargoDetails.packageType')}
                        />
                      )}
                    </Form.Item>
                  </div>
                </Col>
              </div>
            </Col>
          </Row>
          {shipmentData?.cargoType === CargoTypes.Haz && renderHazProductDetails(
            `cargoAndContainerDetails.cargoDetails`,
            {
              colSpan: {
                left: 3,
                right: 5,
              },
              form,
              classes,
              precision: 1,
              min: -999,
              max: 999,
            },
            prefillingData?.cargoAndContainerDetails?.cargoDetails,
          )}
        </Col>
      </Row>
    );
  };

  const renderPackingGroupOrImoClass = (props) => {
    const { key, label, arrayList, rules, initialValue } = props;
    const isSingleCargo = form.getFieldValue('isOnlyOneCargo');
    return (
      <React.Fragment>
        <Col span={3} className={classes.label}>{label}</Col>
        <Col span={isSingleCargo ? 5 : 6}>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator(key, {
              rules,
              initialValue,
            })(
              <Select
                getPopupContainer={(trigger: any) => trigger.parentNode}
                allowClear
                className={classes.selectField}
                placeholder={`Select ${label}`}
              >
                {arrayList?.map(item => (
                  <Select.Option key={item.key}>{item.label}</Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
      </React.Fragment>
    );
  };

  const renderHazProductDetails = (prefix, labelInputProps, initalValuePrefix) : React.ReactElement => {
    const isSingleCargo = form.getFieldValue('isOnlyOneCargo');
    return (
      <div>
        <div className={classes.additionalDetailsCargo}>
          Haz Product Details
        </div>
        <Row>
          {renderPackingGroupOrImoClass({
            key: `${prefix}.productAdditionalDetails.imoClassCode`,
            label: 'IMO Class*',
            arrayList: imoClassCodes,
            rules: [{
              required: true, message: 'Required',
            }],
            initialValue: (initalValuePrefix?.productAdditionalDetails?.imoClassCode) ||
              productIMOClassCodeMap[shipmentData?.cargoSubtype] || undefined
          })}
          <Col span={isSingleCargo ? 1 : 3} />
          {renderLabelAndInput({
            ...labelInputProps,
            label: 'Emergency Contact Name*',
            key: `${prefix}.productAdditionalDetails.emergencyContactName`,
            initialValue: (initalValuePrefix?.productAdditionalDetails?.emergencyContactName) ||
              undefined,
            placeholder: 'Enter Name',
            rules: [
              { required: true, message: 'Required' },
            ],
            labelStyles: { marginTop: '4px' },
          })}
        </Row>
        <Row>
          {renderLabelAndInput({
            ...labelInputProps,
            colSpan: {
              left: 3,
              right: 2,
            },
            label: 'UNDG Number*',
            key: `${prefix}.productAdditionalDetails.undgNumber`,
            initialValue: (initalValuePrefix?.productAdditionalDetails?.undgNumber) ||
              undefined,
            placeholder: 'UNDG Number',
            rules: [
              { required: true, message: 'Required' },
              { max: 4, message: 'Must contain 4 characters' },
              { min: 4, message: 'Must contain 4 characters' },
            ],
          })}
          <Col span={isSingleCargo ? 4 : 7} />
          {renderLabelAndInput({
            ...labelInputProps,
            label: 'Emergency Contact Number*',
            key: `${prefix}.productAdditionalDetails.emergencyContactNumber`,
            initialValue: (initalValuePrefix?.productAdditionalDetails?.emergencyContactNumber) ||
              undefined,
            placeholder: 'Enter Number',
            rules: [
              { required: true, message: 'Required' },
              { max: 512, message: 'Max 512 characters allowed' },
            ],
            labelStyles: { marginTop: '4px' },
          })}
        </Row>
        <Row>
          {renderLabelAndInput({
            ...labelInputProps,
            colSpan: {
              left: 3,
              right: 4,
            },
            label: 'EMS Number',
            key: `${prefix}.productAdditionalDetails.emsNumber`,
            initialValue: (initalValuePrefix?.productAdditionalDetails?.emsNumber) ||
              undefined,
            placeholder: 'Enter EMS Number',
            rules: [
              { max: 6, message: 'Max 6 characters allowed' },
            ],
          })}
          <Col span={isSingleCargo ? 2 : 5} />
          {renderPackingGroupOrImoClass({
            key: `${prefix}.productAdditionalDetails.packingGroup`,
            label: 'Packing Group*',
            arrayList: packingGroups,
            rules: [
              { required: true, message: 'Required' },
            ],
            initialValue: (initalValuePrefix?.productAdditionalDetails?.packingGroup) ||
              undefined
          })}
        </Row>
        <Row>
          {renderLabelAndInputNumber({
            ...labelInputProps,
            colSpan: {
              left: 3,
              right: 2,
            },
            label: 'Flash Point Temp',
            key: `${prefix}.productAdditionalDetails.flashPointTemperature`,
            initialValue: (initalValuePrefix?.productAdditionalDetails?.flashPointTemperature) ??
              undefined,
            placeholder: '0.00',
            rules: [
              {
                validator: (rule, value, cb) => {
                  if (value >= 1000) {
                    cb('Must be less than or equal to 999');
                  } else if (value <= -1000) {
                    cb('Must be more than or equal to -999');
                  } else cb();
                },
              },
            ]
          })}
          <Col span={2} style={{ padding: '8px 0 0 8px' }}>°<span style={{ fontWeight: 600 }}>C</span></Col>
        </Row>
      </div>
    );
  };

  const handleAddMore = (key) => {
    const containerDetails = form.getFieldValue('cargoAndContainerDetails.containerDetails');
    const clonedContainerDetails = isValidArray(containerDetails) ? clone(containerDetails) : [];
    if (clonedContainerDetails[key].sealDetails) {
      clonedContainerDetails[key].sealDetails = [
        ...containerDetails[key].sealDetails,
        {},
      ]
    }
    setState({ containerDetails: clonedContainerDetails });
  };

  const handleRemove = (sealIndex, key) => {
    const containerDetails = form.getFieldValue('cargoAndContainerDetails.containerDetails');
    const clonedContainerDetails = isValidArray(containerDetails) ? clone(containerDetails) : [];
    if (clonedContainerDetails[key].sealDetails) {
      clonedContainerDetails[key].sealDetails.splice(sealIndex, 1)
    }
    setState({ containerDetails: clonedContainerDetails });
  };

  const handleRemoveContainer = (key) => {
    const containerDetails = form.getFieldValue('cargoAndContainerDetails.containerDetails');
    const clonedContainerDetails = isValidArray(containerDetails) ? clone(containerDetails) : [];
    clonedContainerDetails?.splice(key, 1);
    setState({ containerDetails: clonedContainerDetails || [] });
    setFieldsValue({ containerDetails: clonedContainerDetails || [] });
  }

  const getContainers = async (prefillAllContainer?) => {
    let containerList = isValidArray(trackingContainers) ? clone(trackingContainers) : [];
    if (!isValidArray(trackingContainers)) {
      setState({
        isFetchingContainers: true,
        ...(prefillAllContainer && {
          isPrefillingAllContainers: true,
        }),
      });
      const response = await fetchShipmentInstructionsContainers({ shipmentId: shipmentData?.id });
      if (response.isSuccess) {
        containerList = response?.data
      } else {
        setState({ isFetchingContainers: false });
      }
    }
    const containers = containerList?.map(item => ({
      containerNumber: item.containerNumber,
      containerSize: item.containerSize || undefined,
      containerType: item.containerType || undefined,
      containerTareWeight: {
        value: undefined,
        unit: 'kg',
      },
      containerGrossWeight: {
        value: undefined,
        unit: 'kg',
      },
      cargoDetails: {
        packageCount: undefined,
        packageType: undefined,
        packageTypeOnBl: undefined,
        hsCode: undefined,
        grossVolume: {
          value: undefined,
          unit: 'cbm',
        },
        ncmCode: undefined,
        description: undefined,
        marksAndNumbers: undefined,
        ...(shipmentData?.cargoType === CargoTypes.Haz && {
          productAdditionalDetails: {
            imoClassCode: productIMOClassCodeMap[shipmentData?.cargoSubtype] || undefined,
            undgNumber: undefined,
            emsNumber: undefined,
            flashPointTemperature: undefined,
            emergencyContactName: undefined,
            emergencyContactNumber: undefined,
            packingGroup: undefined,
          },
        }),
        ...(shipmentData?.cargoType === CargoType.REEFER && {
          productAdditionalDetails: {
            temperatureControlled: undefined,
            reeferType: 'ACTIVE',
          },
        })
      },
      sealDetails: [{
        type: 'Carrier',
        value: undefined,
      }],
    }))
    setState({
      containerNoList: containers || [],
      isFetchingContainers: false,
      ...(prefillAllContainer && {
        containerDetails: isValidArray(containers) ? containers.slice(0, pendingContainers) : containerDetails,
        isPrefillingAllContainers: false,
      }),
    });
  }

  const renderContainerAndCargoDetails = () : React.ReactElement => {
    return (
      <div style={{ marginTop: 4, backgroundColor: '#fff', padding: 16 }}>
        {trackingContainers?.length >= pendingContainers && <div>
          <span
            className={classes.addMore}
            style={{ marginBottom: '6px', fontWeight: 600 }}
            onClick={() => getContainers(true)}
          >Click here </span>
          to add all
          <span style={{ fontWeight: 600 }}> {pendingContainers} container(s)</span> details from the Booking
          {isValidString(shipmentData?.bookingNumber) &&
            <span style={{ fontWeight: 600 }}> #{shipmentData?.bookingNumber}</span>}
          {isPrefillingAllContainers ? <Spin style={{ marginLeft: '8px' }} /> : ''}
        </div>}
        {containerDetails?.map((item, index) => (
          <div key={index} style={{ backgroundColor: '#fff',
            margin: (form.getFieldValue('isOnlyOneCargo') && shipmentData?.cargoType !== CargoTypes.Haz) ?
            '8px 0 0 0' : '8px 10% 0 0' }}
          >
            <UploadSIModalContainerDetails
              form={form}
              containerKey={index}
              isSingleCargo={form.getFieldValue('isOnlyOneCargo')}
              handleAddMore={handleAddMore}
              handleRemove={handleRemove}
              containerDetails={containerDetails}
              handleRemoveContainer={handleRemoveContainer}
              getPackageTypes={getPackageTypes}
              packageTypes={packageTypes}
              isFetchingPackages={isFetchingPackages}
              shipmentData={shipmentData}
              prefillingData={prefillingData}
              containerNoList={containerNoList}
              isFetchingContainers={isFetchingContainers}
              getContainers={getContainers}
              countryId={countryId}
              pendingContainers={pendingContainers}
              renderHazProductDetails={renderHazProductDetails}
            />
          </div>
        ))}
        <div className={classes.flexRow} style={{ marginTop: 8, background: '#fff', padding: '8px 16px' }}>
          <span className={classes.contentLabel} style={{ marginRight: '12px' }}>Add More Containers</span>
          <Form.Item style={{ width: '5%', margin: '0 8px 0 0' }}>
            {getFieldDecorator('containerCount')(
              <InputNumber
                placeholder="Count"
                precision={0}
                min={0}
                className={classes.inputNumberField}
              />
            )}
          </Form.Item>
          <Button
            className={classes.addContainerButton}
            onClick={handleAddMoreContainer}
            size="small"
            disabled={containerDetails?.length === pendingContainers}
            style={{ marginRight: '8px' }}
          >Add</Button>
        </div>
      </div>
    );
  };

  const handleAddMoreContainer = () : void => {
    const containerCount = getFieldValue('containerCount');
    const containerAddedCount = containerDetails?.length || 0;
    let containerToBeAddedCount = containerCount;
    if ((containerCount + containerAddedCount) > pendingContainers) {
      containerToBeAddedCount = (pendingContainers - containerAddedCount);
    }
    if (containerToBeAddedCount !== containerCount) {
      message.error(`This booking consists of maximum ${pendingContainers} container(s)`, 5);
    }
    if (!containerToBeAddedCount || containerToBeAddedCount <= 0) return;
    const clonedContainerDetails = isValidArray(containerDetails) ? clone(containerDetails) : [];
    for (let i = 0; i < containerToBeAddedCount; i = i + 1) {
      clonedContainerDetails.push({
        sealDetails: [{ type: 'Carrier' }],
      });
    };
    message.success(`${containerToBeAddedCount} more container(s) added successfully`);
    setState({
      containerDetails: clonedContainerDetails,
    });
    setFieldsValue({
      containerCount: undefined,
    });
  }

  const renderCargoType = () : React.ReactElement => {
    return (
      <Row>
        {renderSingeCargoCheckbox()}
        {getFieldValue('isOnlyOneCargo') && renderSingleCargoDetails()}
        {renderContainerAndCargoDetails()}
      </Row>
    );
  };

  const getTotalPackageDetail = () => {
    let total = 0;
    let weight = 0;
    let volume = 0;
    const containerDetails = getFieldValue('cargoAndContainerDetails.containerDetails');
    if (isValidArray(containerDetails)) {
      if (getFieldValue('isOnlyOneCargo')) {
        const volumeObj = getFieldValue('cargoAndContainerDetails.cargoDetails.grossVolume');
        containerDetails?.forEach((item, index) => {
          const packageCount =
            getFieldValue(`cargoAndContainerDetails.containerDetails[${index}].packageCount`);
          if (isValidNumber(packageCount)) {
            total += packageCount;
          }
          const weightObj = getFieldValue(`cargoAndContainerDetails.containerDetails[${index}].grossWeight`);
          if (isValidNumber(weightObj?.value)){
            weight += (weightObj?.value * weightConversionMap[weightObj?.unit].kg)
          }
        });
        if (isValidNumber(volumeObj?.value)) {
          volume += (volumeObj?.value * volumeConversionMap[volumeObj?.unit].cbm)
        }
      } else {
        containerDetails?.forEach((item, index) => {
          const packageCount =
            getFieldValue(`cargoAndContainerDetails.containerDetails[${index}].cargoDetails.packageCount`);
          const weightObj =
            getFieldValue(`cargoAndContainerDetails.containerDetails[${index}].cargoDetails.grossWeight`);
          const volumeObj =
            getFieldValue(`cargoAndContainerDetails.containerDetails[${index}].cargoDetails.grossVolume`);
          if (isValidNumber(packageCount)) {
            total += packageCount;
          }
          if (isValidNumber(weightObj?.value)){
            weight += (weightObj?.value * weightConversionMap[weightObj?.unit].kg)
          }
          if (isValidNumber(volumeObj?.value)) {
            volume += (volumeObj?.value * volumeConversionMap[volumeObj?.unit].cbm)
          }
        });
      }
    }

    return ({ total, weight, volume });
  }

  const renderContainerCargoTotal = () : React.ReactElement => {
    const packageDetails = getTotalPackageDetail();

    return (
      <div className={classes.totalContainerCargo}>
        <strong style={{ marginRight: '12px' }}>Container Details</strong>
        <span style={{ fontSize: '12px' }}>Total </span>
        <span style={{ fontSize: '16px', fontWeight: 600 }}> {containerDetails?.length}</span>
        <span style={{ margin: '0 15px' }}>|</span>
        <strong style={{ marginRight: '12px' }}>Packaging Details</strong>
        <span style={{ fontSize: '12px' }}>Total </span>
        <span style={{ fontSize: '16px', fontWeight: 600 }}> {packageDetails?.total || '0'}</span>
        <span style={{ margin: '0 12px' }}>|</span>
        <span style={{ fontSize: '12px' }}>Cargo Gross Weight </span>
        <span style={{ fontSize: '16px', fontWeight: 600 }}> {packageDetails?.weight || '0'}kg</span>
        <span style={{ margin: '0 12px' }}>|</span>
        <span style={{ fontSize: '12px' }}>Cargo Gross Volume </span>
        <strong style={{ fontSize: '16px', fontWeight: 600 }}> {packageDetails?.volume || '0'}cbm</strong>
      </div>
    );
  };

  return (
    <React.Fragment>
      {renderCargoType()}
      {renderContainerCargoTotal()}
    </React.Fragment>
  );
};

const mapStateToProps = ({ masterData, shipmentExecutionReducer }, ownProps) => ({
  weightUnits: masterData.execution?.validWeightUnits,
  volumeUnits: masterData.execution?.validVolumeUnits,
  volumeConversionMap: masterData.shippingInstructionsMasterData?.volumeConversionMap,
  weightConversionMap: masterData.shippingInstructionsMasterData?.weightConversionMap,
  SICountryValidations: masterData.shippingInstructionsMasterData?.SICountryValidations,
  pendingContainers: shipmentExecutionReducer.siPendingCount?.SI?.pendingContainerCount,
  productIMOClassCodeMap: masterData.shippingInstructionsMasterData?.productIMOClassCodeMap,
  packingGroups: masterData.shippingInstructionsMasterData?.packingGroup,
  imoClassCodes: masterData.shippingInstructionsMasterData?.imoClassCodes,
});

export default connect(
  mapStateToProps,
  null,
  )(React.memo(withStyles(uploadSIModalStyles)(UploadSIModalContainerAndCargo)));